import { useEffect, useRef, useCallback } from 'react'

interface UseInactivityTimeoutOptions {
  timeout: number // timeout in milliseconds
  onTimeout: () => void
  onWarning?: (secondsRemaining: number) => void // called when warning period starts
  warningTime?: number // warning time in milliseconds (default: 60000 = 1 minute)
  isActive: boolean // whether the timeout should be active
}

export function useInactivityTimeout({
  timeout,
  onTimeout,
  onWarning,
  warningTime = 60000,
  isActive,
}: UseInactivityTimeoutOptions) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())
  const warningShownRef = useRef<boolean>(false) // Track if warning has been shown for current timeout period
  const isActiveRef = useRef<boolean>(isActive)
  const initialized = useRef<boolean>(false)

  // Clear all timeouts
  const clearInactivityTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current)
      warningTimeoutRef.current = null
    }
  }, [])

  // Reset the timeout
  const resetTimeout = useCallback(() => {
    if (!isActiveRef.current) {
      return
    }

    lastActivityRef.current = Date.now()

    // Clear all existing timeouts
    clearInactivityTimeout()

    // Reset warning shown flag when timeout is reset due to activity
    warningShownRef.current = false

    // Set warning timeout if warning callback is provided
    if (onWarning && warningTime < timeout) {
      const warningDelay = timeout - warningTime

      warningTimeoutRef.current = setTimeout(() => {
        // Only show warning if still active and it hasn't been shown yet for this timeout period
        if (isActiveRef.current && !warningShownRef.current) {
          const remainingSeconds = Math.ceil(warningTime / 1000)

          // Mark warning as shown for this timeout period
          warningShownRef.current = true

          // Call warning callback only once with the initial countdown value
          onWarning(remainingSeconds)
        }
      }, warningDelay)
    }

    // Set main timeout
    timeoutRef.current = setTimeout(() => {
      // Only call timeout if still active
      if (isActiveRef.current) {
        console.log('[InactivityTimeout] Session expired due to inactivity after', timeout / 1000, 'seconds')
        onTimeout()
      }
    }, timeout)
  }, [timeout, onTimeout, onWarning, warningTime, clearInactivityTimeout])

  // Activity event handler
  const handleActivity = useCallback(() => {
    if (isActiveRef.current) {
      resetTimeout()
    }
  }, [resetTimeout])

  useEffect(() => {
    // Update the ref to track current isActive state
    isActiveRef.current = isActive

    if (!isActive) {
      clearInactivityTimeout()
      initialized.current = false
      return
    }

    // Only initialize once when becoming active
    if (!initialized.current) {
      initialized.current = true

      // Events to track for user activity
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown']

      // Add event listeners
      events.forEach((event) => {
        document.addEventListener(event, handleActivity, { passive: true })
      })

      resetTimeout()

      // Cleanup
      return () => {
        events.forEach((event) => {
          document.removeEventListener(event, handleActivity)
        })
        clearInactivityTimeout()
        initialized.current = false
      }
    }

    // Return empty cleanup function for other cases
    return () => {}
  }, [handleActivity, resetTimeout, clearInactivityTimeout, isActive])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return {
    resetTimeout,
    clearInactivityTimeout,
    getLastActivity: () => lastActivityRef.current,
  }
}
